import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  Divider,
  Heading,
  Badge,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { usePosts, useUserPosts, useConversations } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

export const ApiIntegrationTest: React.FC = () => {
  const toast = useToast();
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState<{ [key: string]: any }>({});

  // Test Posts API
  const { data: postsData, isLoading: postsLoading, error: postsError, refetch: refetchPosts } = usePosts({
    limit: 5,
  });

  // Test User Posts API (only if authenticated)
  const { 
    data: userPostsData, 
    isLoading: userPostsLoading, 
    error: userPostsError, 
    refetch: refetchUserPosts 
  } = useUserPosts(user?.userId || '');

  // Test Conversations API (only if authenticated)
  const { 
    data: conversationsData, 
    isLoading: conversationsLoading, 
    error: conversationsError, 
    refetch: refetchConversations 
  } = useConversations();

  const runAllTests = async () => {
    const results: { [key: string]: any } = {};

    // Test 1: Posts API
    try {
      await refetchPosts();
      results.posts = {
        status: 'success',
        data: postsData,
        error: null,
      };
    } catch (error) {
      results.posts = {
        status: 'error',
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }

    // Test 2: User Posts API (if authenticated)
    if (isAuthenticated && user?.userId) {
      try {
        await refetchUserPosts();
        results.userPosts = {
          status: 'success',
          data: userPostsData,
          error: null,
        };
      } catch (error) {
        results.userPosts = {
          status: 'error',
          data: null,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    } else {
      results.userPosts = {
        status: 'skipped',
        data: null,
        error: 'Not authenticated',
      };
    }

    // Test 3: Conversations API (if authenticated)
    if (isAuthenticated) {
      try {
        await refetchConversations();
        results.conversations = {
          status: 'success',
          data: conversationsData,
          error: null,
        };
      } catch (error) {
        results.conversations = {
          status: 'error',
          data: null,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    } else {
      results.conversations = {
        status: 'skipped',
        data: null,
        error: 'Not authenticated',
      };
    }

    setTestResults(results);

    toast({
      title: 'API Tests Completed',
      description: 'Check the results below',
      status: 'info',
      duration: 3000,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'green';
      case 'error': return 'red';
      case 'skipped': return 'yellow';
      default: return 'gray';
    }
  };

  return (
    <Box p={6} maxW="container.lg" mx="auto">
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>API Integration Test</Heading>
          <Text color="gray.600">
            Test the integration between frontend and backend APIs
          </Text>
        </Box>

        {/* Authentication Status */}
        <Alert status={isAuthenticated ? 'success' : 'warning'}>
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold">
              Authentication Status: {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </Text>
            {user && (
              <Text fontSize="sm">
                Logged in as: {user.firstName} {user.lastName} ({user.email})
              </Text>
            )}
          </VStack>
        </Alert>

        {/* Test Controls */}
        <HStack>
          <Button colorScheme="blue" onClick={runAllTests}>
            Run All API Tests
          </Button>
          <Button variant="outline" onClick={() => setTestResults({})}>
            Clear Results
          </Button>
        </HStack>

        <Divider />

        {/* Live API Status */}
        <Box>
          <Heading size="md" mb={4}>Live API Status</Heading>
          <VStack spacing={4} align="stretch">
            {/* Posts API */}
            <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold">Posts API</Text>
                <Badge colorScheme={postsError ? 'red' : postsLoading ? 'yellow' : 'green'}>
                  {postsError ? 'Error' : postsLoading ? 'Loading' : 'Ready'}
                </Badge>
              </HStack>
              {postsError && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">{postsError.message}</Text>
                </Alert>
              )}
              {postsData && (
                <Text fontSize="sm" color="gray.600">
                  Loaded {postsData.items?.length || 0} posts (Total: {postsData.totalCount || 0})
                </Text>
              )}
            </Box>

            {/* User Posts API */}
            <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold">User Posts API</Text>
                <Badge colorScheme={
                  !isAuthenticated ? 'gray' : 
                  userPostsError ? 'red' : 
                  userPostsLoading ? 'yellow' : 'green'
                }>
                  {!isAuthenticated ? 'Disabled' : 
                   userPostsError ? 'Error' : 
                   userPostsLoading ? 'Loading' : 'Ready'}
                </Badge>
              </HStack>
              {userPostsError && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">{userPostsError.message}</Text>
                </Alert>
              )}
              {userPostsData && (
                <Text fontSize="sm" color="gray.600">
                  Loaded {userPostsData.items?.length || 0} user posts (Total: {userPostsData.totalCount || 0})
                </Text>
              )}
            </Box>

            {/* Conversations API */}
            <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="bold">Conversations API</Text>
                <Badge colorScheme={
                  !isAuthenticated ? 'gray' : 
                  conversationsError ? 'red' : 
                  conversationsLoading ? 'yellow' : 'green'
                }>
                  {!isAuthenticated ? 'Disabled' : 
                   conversationsError ? 'Error' : 
                   conversationsLoading ? 'Loading' : 'Ready'}
                </Badge>
              </HStack>
              {conversationsError && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">{conversationsError.message}</Text>
                </Alert>
              )}
              {conversationsData && (
                <Text fontSize="sm" color="gray.600">
                  Loaded {conversationsData.items?.length || 0} conversations (Total: {conversationsData.totalCount || 0})
                </Text>
              )}
            </Box>
          </VStack>
        </Box>

        {/* Test Results */}
        {Object.keys(testResults).length > 0 && (
          <>
            <Divider />
            <Box>
              <Heading size="md" mb={4}>Test Results</Heading>
              <VStack spacing={4} align="stretch">
                {Object.entries(testResults).map(([testName, result]) => (
                  <Box key={testName} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="bold" textTransform="capitalize">
                        {testName.replace(/([A-Z])/g, ' $1')}
                      </Text>
                      <Badge colorScheme={getStatusColor(result.status)}>
                        {result.status}
                      </Badge>
                    </HStack>
                    
                    {result.error && (
                      <Alert status="error" size="sm" mb={2}>
                        <AlertIcon />
                        <Text fontSize="sm">{result.error}</Text>
                      </Alert>
                    )}
                    
                    {result.data && (
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Response Data:</Text>
                        <Textarea
                          value={JSON.stringify(result.data, null, 2)}
                          readOnly
                          size="sm"
                          maxH="200px"
                          fontFamily="mono"
                          fontSize="xs"
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </VStack>
            </Box>
          </>
        )}

        {/* API Configuration */}
        <Divider />
        <Box>
          <Heading size="md" mb={4}>API Configuration</Heading>
          <Box p={4} bg="gray.50" borderRadius="md">
            <VStack align="start" spacing={2}>
              <Text fontSize="sm">
                <strong>Base URL:</strong> {import.meta.env.VITE_API_BASE_URL || 'Not configured'}
              </Text>
              <Text fontSize="sm">
                <strong>Environment:</strong> {import.meta.env.MODE}
              </Text>
              <Text fontSize="sm">
                <strong>Build Time:</strong> {new Date().toISOString()}
              </Text>
            </VStack>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}; 